import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { TablePagination, useMediaQuery } from '@material-ui/core'

import { Container, ContentHeader, LoadingFeedback, Page } from 'components'
import { DocumentShowForm, DocumentTabs } from './components'

import helpers from 'helpers'
import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'
import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import { routes } from 'Routes'
import * as service from 'service'
import constants from 'constants/index'

const DocumentsShow = ({ match }) => {
  const { documentId } = match?.params

  const { DOCUMENTS_CATEGORY_ID, WRITTEN_CONTENT_KIND } = constants.documents
  const { CONSENTS, CONSENT_RECEIPTS } = constants.permissions

  const [currentTab, setCurrentTab] = useState(DOCUMENTS_CATEGORY_ID)
  const [hasPermission, setHasPermission] = useState(false)
  const [loading, setLoading] = useState(false)

  const history = useHistory()
  const permissions = usePermissions()

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const {
    page: documentVersionsPage,
    perPage: documentVersionsPerPage,
    handleChangePage: handleChangeDocumentVersionsPage,
    handleChangeRowsPerPage: handleChangeDocumentVersionsRowsPerPage,
  } = usePagination({ localState: true, initialPerPage: 5 })

  const { response, isLoading } = useFetch(
    service.dponet.privacyPolicies.get,
    { privacyPolicyId: documentId },
    [documentId],
  )

  const {
    response: documentVersionsResponse,
    isLoading: documentVersionsIsLoading,
    refresh,
  } = useFetch(
    service.dponet.privacyPolicies.listVersions,
    {
      privacyPolicyId: documentId,
      minimal: true,
      page: documentVersionsPage,
      perPage: documentVersionsPerPage,
    },
    [documentId, documentVersionsPage, documentVersionsPerPage],
    !!documentId,
  )
  const {
    response: privacyPermissionsResponse,
    isLoading: permissionsIsLoading,
  } = useFetch(
    service.dponet.privacyPolicies.getPrivacyPermissions,
    { privacyPolicyId: documentId },
    [documentId],
    !!documentId,
  )

  const {
    page: linkedDocumentsPage,
    perPage: linkedDocumentsPerPage,
    handleChangePage: handleChangeLinkedDocumentsPage,
    handleChangeRowsPerPage: handleChangeLinkedDocumentsRowsPerPage,
  } = usePagination({ localState: true, initialPerPage: 5 })

  const {
    response: linkedDocumentsResponse,
    isLoading: linkedDocumentsIsLoading,
  } = useFetch(
    service.dponet.privacyPolicies.listLinkedDocuments,
    {
      privacyPolicyId: documentId,
      page: linkedDocumentsPage,
      perPage: linkedDocumentsPerPage,
    },
    [documentId, linkedDocumentsPage, linkedDocumentsPerPage],
    !!documentId,
  )

  const linkedDocuments = linkedDocumentsResponse?.data?.linkedDocuments || []

  const documentVersions =
    documentVersionsResponse?.data?.documentVersions || []

  const privacyPolicy = response?.data?.privacyPolicy
  const hasCollectApproval = privacyPolicy?.collectApproval
  const hasCollectAccepted = privacyPolicy?.collectAccepted
  const isWrittenContent = privacyPolicy?.contentKind === WRITTEN_CONTENT_KIND
  const expirationConfigs = privacyPolicy?.privacyPolicyExpirationConfig || {}

  const privacyPermissions =
    privacyPermissionsResponse?.data?.privacyPermissions || []
  const selectedUsers = privacyPermissions
    .filter((p) => p.targetType === constants.documents.TARGET_TYPE_USER)
    .map((p) => p.target)
  const selectedDepartments = privacyPermissions
    .filter((p) => p.targetType === constants.documents.TARGET_TYPE_DEPARTMENT)
    .map((p) => p.target)
  const hasConsentForm = !!privacyPolicy?.consentFormId
  const disabledAcceptedTab =
    !hasCollectAccepted || !hasPermission || !hasConsentForm

  const disabledAcceptedTooltip =
    {
      [!hasCollectAccepted]:
        'Você não pode visualizar os aceites se não estiver coletando!',
      [!hasConsentForm]: 'Este documento não possui formulário de aceites.',
      [!hasPermission]:
        'Você não possui permissões suficientes para visualizar os consentimentos. Caso precise de acesso, entre em contato com um superior da empresa.',
    }[true] ?? ''

  const handleBackRedirection = () => history.push(routes.documents.all)

  const downloadDocument = async (documentVersionId) => {
    try {
      setLoading(true)

      const res = await service.dponet.documentVersions.exportDocument({
        documentVersionId,
      })

      const file = await helpers.file.fromUrl(
        res?.data?.fileUrl,
        `documento-versao-${documentVersionId}.pdf`,
      )

      helpers.file.handleDownload(file)

      refresh()
    } catch {
      // Já é tratado em outra função
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (
      permissions.permitted(CONSENTS.LIST) &&
      permissions.permitted(CONSENT_RECEIPTS.LIST)
    )
      setHasPermission(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const versionTablePagination = (
    <TablePagination
      component="div"
      count={documentVersionsResponse?.data?.meta?.totalCount || 0}
      onPageChange={handleChangeDocumentVersionsPage}
      onRowsPerPageChange={handleChangeDocumentVersionsRowsPerPage}
      page={documentVersionsPage - 1}
      rowsPerPage={documentVersionsPerPage}
      rowsPerPageOptions={[5, 10, 25, 100]}
      labelRowsPerPage={isDesktop ? 'Por página' : ''}
      nextIconButtonProps={{ size: 'small' }}
      backIconButtonProps={{ size: 'small' }}
    />
  )

  return (
    <Page title="Visualizar documento">
      <Container maxWidth={false}>
        <LoadingFeedback open={loading || isLoading} />
        <ContentHeader title="Visualizar documento" goBack />
        <DocumentTabs
          currentTab={currentTab}
          onChange={(_, tab) => setCurrentTab(tab)}
          showSettingsTab={isWrittenContent}
          showAcceptedTab={isWrittenContent}
          showApprovalTab={isWrittenContent}
          showControlsTab={isWrittenContent}
          showHistoryTab={isWrittenContent}
          disabledAcceptedTab={disabledAcceptedTab}
          disabledAcceptedTooltip={disabledAcceptedTooltip}
          disabledApprovalTab={!hasCollectApproval}
        />
        {!isLoading && (
          <DocumentShowForm
            currentTab={currentTab}
            isLoading={isLoading || permissionsIsLoading}
            currentDocument={privacyPolicy}
            documentId={documentId}
            isWrittenContent={isWrittenContent}
            expirationConfigs={expirationConfigs}
            permissions={{ selectedUsers, selectedDepartments }}
            pageActions={{ handleBackRedirection, setLoading }}
            versions={{
              documentVersions,
              isLoading: documentVersionsIsLoading,
              downloadDocument,
              pagination: versionTablePagination,
            }}
            controls={{
              linkedDocuments,
              isLoading: linkedDocumentsIsLoading,
              pagination: {
                page: linkedDocumentsPage,
                perPage: linkedDocumentsPerPage,
                totalCount: linkedDocumentsResponse?.data?.meta?.totalCount,
                onPageChange: handleChangeLinkedDocumentsPage,
                onRowsPerPageChange: handleChangeLinkedDocumentsRowsPerPage,
              },
            }}
          />
        )}
      </Container>
    </Page>
  )
}

export default DocumentsShow
